/**
 * Enhanced topic drift indicator component - prominent floating transparent style with content-based detection
 */

import React, { useState, useEffect } from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { AlertIcon, CheckCircleIcon, CloseIcon } from './SvgConsolidated/SvgIcons';

interface TopicDriftIndicatorProps {
  isActive: boolean;
  ageProfile: AgeProfile;
  contentSimilarity?: number;
  storyRelevance?: number;
  driftScore?: number;
  onDismiss?: () => void;
}

export default function TopicDriftIndicator({ 
  isActive, 
  ageProfile, 
  contentSimilarity = 0, 
  storyRelevance = 0,
  driftScore = 0,
  onDismiss 
}: TopicDriftIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'enter' | 'pulse' | 'exit'>('enter');

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      setAnimationPhase('enter');
      
      // Enter animation, then pulse
      const enterTimer = setTimeout(() => {
        setAnimationPhase('pulse');
      }, 500);
      
      // Auto-dismiss after 8 seconds if not manually dismissed
      const dismissTimer = setTimeout(() => {
        handleDismiss();
      }, 8000);
      
      return () => {
        clearTimeout(enterTimer);
        clearTimeout(dismissTimer);
      };
    } else {
      handleDismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleDismiss = () => {
    setAnimationPhase('exit');
    setTimeout(() => {
      setIsVisible(false);
      if (onDismiss) {
        onDismiss();
      }
    }, 300);
  };

  if (!isVisible) return null;

  // Determine severity based on content analysis
  const getSeverityLevel = () => {
    if (contentSimilarity < 0.1) return 'severe';
    if (driftScore > 2 || storyRelevance === 0) return 'moderate';
    return 'mild';
  };

  const severity = getSeverityLevel();
  
  // Age-appropriate messaging
  const getMessage = () => {
    const maxAge = ageProfile.age_range.max;
    if (maxAge <= 8) {
      return severity === 'severe' 
        ? "🚨 Let's talk about our story! 📚" 
        : "📖 Back to our adventure!";
    } else if (maxAge <= 11) {
      return severity === 'severe'
        ? "⚠️ Hey! Let's focus on your story! 📖"
        : "🎯 Story time - let's stay on track!";
    } else {
      return severity === 'severe'
        ? "🔴 Off-topic detected - Back to your story! 📚"
        : "🟡 Conversation drifting - Let's refocus!";
    }
  };

  const getSubMessage = () => {
    const maxAge = ageProfile.age_range.max;
    if (contentSimilarity < 0.1) {
      return maxAge <= 8 
        ? "I'm here to help with reading!" 
        : "Let's discuss what's happening in your story";
    } else if (driftScore > storyRelevance) {
      return maxAge <= 8
        ? "Tell me about the characters!"
        : "What's your favorite part so far?";
    } else {
      return maxAge <= 8
        ? "Keep reading with me!"
        : "Stay engaged with your story";
    }
  };

  // Dynamic styling based on severity
  const getIndicatorColors = () => {
    switch (severity) {
      case 'severe':
        return {
          bg: 'bg-red-500/20',
          border: 'border-red-500/40',
          text: 'text-red-700',
          icon: 'text-red-600',
          accent: 'bg-red-500'
        };
      case 'moderate':
        return {
          bg: 'bg-orange-500/20',
          border: 'border-orange-500/40',
          text: 'text-orange-700',
          icon: 'text-orange-600',
          accent: 'bg-orange-500'
        };
      default:
        return {
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500/40',
          text: 'text-yellow-700',
          icon: 'text-yellow-600',
          accent: 'bg-yellow-500'
        };
    }
  };

  const colors = getIndicatorColors();

  return (
    <>
      {/* Backdrop blur overlay */}
      <div 
        className={`fixed inset-0 bg-black/10 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          animationPhase === 'enter' ? 'opacity-0 animate-[fadeIn_0.5s_ease-out_forwards]' :
          animationPhase === 'exit' ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleDismiss}
      />
      
      {/* Main floating indicator */}
      <div className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 
        transition-all duration-500 ease-out ${
          animationPhase === 'enter' ? 'opacity-0 scale-75 animate-[bounceIn_0.5s_ease-out_0.2s_forwards]' :
          animationPhase === 'exit' ? 'opacity-0 scale-75' : 
          'opacity-100 scale-100'
        } ${animationPhase === 'pulse' ? 'animate-pulse' : ''}`}>
        
        <div className={`relative max-w-md mx-4 ${colors.bg} ${colors.border} border-2 rounded-3xl shadow-2xl backdrop-blur-md`}>
          {/* Animated accent bar */}
          <div className={`absolute top-0 left-0 right-0 h-2 ${colors.accent} rounded-t-3xl 
            ${severity === 'severe' ? 'animate-pulse' : ''}`} />
          
          {/* Content */}
          <div className="p-6 pt-8">
            <div className="flex items-start gap-4">
              {/* Warning icon with animation */}
              <div className={`flex-shrink-0 p-3 rounded-full ${colors.bg} ${colors.border} border 
                ${severity === 'severe' ? 'animate-bounce' : 'animate-pulse'}`}>
                {severity === 'severe' ? (
                  <AlertIcon className={`w-8 h-8 ${colors.icon}`} />
                ) : (
                  <CheckCircleIcon className={`w-8 h-8 ${colors.icon}`} />
                )}
              </div>
              
              {/* Message content */}
              <div className="flex-1 min-w-0">
                <h3 className={`${ageProfile.theme.typography.bodySize} font-bold ${colors.text} leading-tight`}>
                  {getMessage()}
                </h3>
                <p className={`text-sm ${colors.text} opacity-80 mt-2 leading-relaxed`}>
                  {getSubMessage()}
                </p>
                
                {/* Content analysis feedback for older kids */}
                {ageProfile.age_range.max > 11 && (
                  <div className="mt-3 text-xs opacity-70 space-y-1">
                    <div className={`${colors.text}`}>
                      Story similarity: {Math.round(contentSimilarity * 100)}% • 
                      Relevance score: {Math.round(storyRelevance)}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className={`flex-shrink-0 p-2 rounded-full ${colors.bg} hover:bg-opacity-80 
                  transition-all duration-200 hover:scale-110`}
                aria-label="Dismiss alert"
              >
                <CloseIcon className={`w-5 h-5 ${colors.icon}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.3);
          }
          50% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.05);
          }
          70% {
            transform: translate(-50%, -50%) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}} />
    </>
  );
}